import React, { useEffect, useState } from 'react';

import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  makeStyles,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import general_messages from 'messages/general_messages';

const useStyles = makeStyles(theme => ({
  root: {},
  optionsContainer: {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(3),
  },
}));

export type PrintOptions = {
  title: boolean;
  description: boolean;
  metadata: boolean;
  specialInstruction: boolean;
  instanceComments: boolean;
  instanceLogs: boolean;
  steps: boolean;
  stepComments: boolean;
  stepActivityLogs: boolean;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onPrint: (options: PrintOptions) => void;
};

const DEFAULT_OPTIONS: PrintOptions = {
  title: true,
  description: true,
  metadata: true,
  specialInstruction: true,
  instanceComments: true,
  instanceLogs: true,
  steps: true,
  stepComments: true,
  stepActivityLogs: true,
};

const SUMMARY_OPTIONS: PrintOptions = {
  title: true,
  description: true,
  metadata: true,
  specialInstruction: true,
  instanceComments: false,
  instanceLogs: false,
  steps: true,
  stepComments: false,
  stepActivityLogs: false,
};

/** Sub-options that only make sense when Steps is on; clearing Steps clears these (user must uncheck Steps explicitly). */
const STEP_SUB_OPTION_KEYS = ['stepComments', 'stepActivityLogs'] as const;

const isStepSubOptionKey = (key: keyof PrintOptions): key is typeof STEP_SUB_OPTION_KEYS[number] =>
  (STEP_SUB_OPTION_KEYS as readonly string[]).includes(key);

const withStepSubOptionsCleared = (base: PrintOptions): PrintOptions =>
  STEP_SUB_OPTION_KEYS.reduce<PrintOptions>(
    (acc, key) => ({
      ...acc,
      [key]: false,
    }),
    base,
  );

const PrintOptionsDialog: React.FC<Props> = ({ open, onClose, onPrint }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const [mode, setMode] = useState<'summary' | 'custom'>('summary');
  const [customOptions, setCustomOptions] = useState<PrintOptions>(DEFAULT_OPTIONS);

  // Reset state when opening
  useEffect(() => {
    if (open) {
      setMode('summary');
      setCustomOptions(DEFAULT_OPTIONS);
    }
  }, [open]);

  const handleModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMode(event.target.value as 'summary' | 'custom');
  };

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name as keyof PrintOptions;
    const { checked } = event.target;

    setCustomOptions(prev => {
      const draft = { ...prev, [name]: checked };

      if (name === 'steps' && !checked) {
        return withStepSubOptionsCleared(draft);
      }
      if (isStepSubOptionKey(name) && checked) {
        return { ...draft, steps: true };
      }
      return draft;
    });
  };

  const handlePrint = () => {
    const options = mode === 'summary' ? SUMMARY_OPTIONS : customOptions;
    onPrint(options);
  };

  return (
    <Dialog fullWidth maxWidth='xs' onClose={onClose} open={open}>
      <DialogTitle>{t(general_messages.print_mode)}</DialogTitle>
      <DialogContent>
        <FormControl component='fieldset'>
          <RadioGroup aria-label='print-mode' name='printMode' onChange={handleModeChange} value={mode}>
            <FormControlLabel control={<Radio />} label={t(general_messages.print_summary)} value='summary' />
            <FormControlLabel control={<Radio />} label={t(general_messages.print_custom)} value='custom' />
          </RadioGroup>
        </FormControl>

        {mode === 'custom' && (
          <div className={styles.optionsContainer}>
            <FormGroup>
              <FormControlLabel
                control={<Checkbox checked={customOptions.title} name='title' onChange={handleOptionChange} />}
                label={t(general_messages.print_instance_name)}
              />
              <FormControlLabel
                control={<Checkbox checked={customOptions.description} name='description' onChange={handleOptionChange} />}
                label={t(general_messages.print_description)}
              />
              <FormControlLabel
                control={<Checkbox checked={customOptions.metadata} name='metadata' onChange={handleOptionChange} />}
                label={t(general_messages.print_metadata)}
              />
              <FormControlLabel
                control={<Checkbox checked={customOptions.specialInstruction} name='specialInstruction' onChange={handleOptionChange} />}
                label={t(general_messages.print_special_instructions)}
              />
              <FormControlLabel
                control={<Checkbox checked={customOptions.instanceComments} name='instanceComments' onChange={handleOptionChange} />}
                label={t(general_messages.print_instance_comments)}
              />
              <FormControlLabel
                control={<Checkbox checked={customOptions.instanceLogs} name='instanceLogs' onChange={handleOptionChange} />}
                label={t(general_messages.print_instance_logs)}
              />
              <FormControlLabel
                control={<Checkbox checked={customOptions.steps} name='steps' onChange={handleOptionChange} />}
                label={t(general_messages.print_steps)}
              />
              <FormControlLabel
                control={<Checkbox checked={customOptions.stepComments} name='stepComments' onChange={handleOptionChange} />}
                label={t(general_messages.print_step_comments)}
              />
              <FormControlLabel
                control={<Checkbox checked={customOptions.stepActivityLogs} name='stepActivityLogs' onChange={handleOptionChange} />}
                label={t(general_messages.print_step_activity_logs)}
              />
            </FormGroup>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t(general_messages.cancel)}</Button>
        <Button color='primary' onClick={handlePrint} variant='contained'>
          {t(general_messages.print)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrintOptionsDialog;
